import { Payment } from 'payments-lib-types';
import { ScPayments, ScPaymentsMethods } from './styled';
import { Spinner } from 'components/Spinner';
import { PaymentMethodCard } from '../PaymentMethod/components/PaymentMethodCard';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { FormApi } from 'informed';
import { ScNotFound } from '../PaymentMethodsNotFound/styled';
import { CRYPTO_METHOD } from '../PaymentMethod/helpers';
import { Button } from 'components/buttons';
import { useToggle } from '@repo-level/core-front';
import { LocalStoragePaymentMethod } from '../helpers';

interface MethodApi {
  method: string;
}

interface PaymentsMethodsProps {
  pending: boolean;
  onSelect: (method: Payment.Method) => void;
  currentMethod?: Payment.Method;
  methods?: Payment.Method[];
}

export const PaymentsMethods = React.memo(
  ({
    currentMethod,
    methods = [],
    pending,
    onSelect,
  }: PaymentsMethodsProps) => {
    const { t } = useTranslation(['deposits', 'glossary', 'verification']);
    const { isMobile } = useContext(CommonUtilsContext);
    const formApiRef = useRef<FormApi<MethodApi>>();
    const [opened, toggleOpened] = useToggle();

    const initialSelectedMethodId = LocalStoragePaymentMethod.get();

    const [selectedMethodId, setSelectedMethodId] = useState(
      initialSelectedMethodId
    );
    const [shouldSort, setShouldSort] = useState(!!initialSelectedMethodId);
    const [sortedPaymentMethods, setSortedPaymentMethods] = useState<
      Payment.Method[]
    >([]);

    const { fiatMethods, cryptoMethods } = useMemo(() => {
      return methods.reduce<{
        fiatMethods: Payment.Method[];
        cryptoMethods: Payment.Method[];
      }>(
        (acc, method) => {
          (method.type.includes(CRYPTO_METHOD)
            ? acc.cryptoMethods
            : acc.fiatMethods
          ).push(method);
          return acc;
        },
        { fiatMethods: [], cryptoMethods: [] }
      );
    }, [methods]);

    useEffect(() => {
      if (!methods || methods.length === 0) return;

      if (shouldSort && selectedMethodId) {
        const priority = methods.find((m) => m.id === selectedMethodId);

        if (priority) {
          const newOrder = [
            priority,
            ...methods.filter((m) => m.id !== priority.id),
          ];
          setSortedPaymentMethods(newOrder);
        } else {
          setSortedPaymentMethods(methods);
        }

        setShouldSort(false);
      } else {
        if (sortedPaymentMethods.length === methods.length) return;

        setSortedPaymentMethods(methods);
      }
    }, [methods, shouldSort, selectedMethodId, sortedPaymentMethods.length]);

    useEffect(() => {
      if (!currentMethod && selectedMethodId) {
        const saved = methods.find((m) => m.id === selectedMethodId);
        if (saved) onSelect(saved);
      }
    }, [methods, currentMethod, selectedMethodId, onSelect]);

    useEffect(() => {
      if (!currentMethod?.id) return;

      LocalStoragePaymentMethod.push(currentMethod.id);
      setSelectedMethodId(currentMethod.id);

      if (currentMethod.id !== selectedMethodId) {
        setShouldSort(false);
      }
    }, [currentMethod?.id, selectedMethodId]);

    const handleToggle = useCallback(() => {
      if (opened && currentMethod?.id) setShouldSort(true);
      toggleOpened();
    }, [opened, currentMethod?.id, toggleOpened]);

    useEffect(() => {
      if (formApiRef.current && currentMethod?.id) {
        formApiRef.current.setValue('method', currentMethod.id);
      }
    }, [currentMethod?.id]);

    return (
      <ScPaymentsMethods>
        {pending ? (
          <Spinner size={64} center />
        ) : !methods || methods.length === 0 ? (
          <ScNotFound>{t('deposits:notFoundPaymentsMethods')}</ScNotFound>
        ) : (
          <>
            {isMobile ? (
              <div className="mobile-payments-wrapper">
                <ScPayments expanded={!opened}>
                  {sortedPaymentMethods.map((method, index) => (
                    <PaymentMethodCard
                      key={method.id}
                      method={method}
                      index={index}
                      onSelect={onSelect}
                      currentMethod={currentMethod}
                    />
                  ))}
                </ScPayments>
                <div className="button-wrapper">
                  <Button grayDarker small onClick={handleToggle}>
                    {opened
                      ? t('verification:showLess')
                      : t('verification:showMore')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {fiatMethods && (
                  <>
                    <h3>{t('glossary:payments')}</h3>
                    <ScPayments>
                      {fiatMethods.map((method, index) => (
                        <PaymentMethodCard
                          key={method.id}
                          method={method}
                          index={index}
                          onSelect={onSelect}
                          currentMethod={currentMethod}
                        />
                      ))}
                    </ScPayments>
                  </>
                )}
                {cryptoMethods && (
                  <>
                    <h3 className="crypto-title">
                      {t('glossary:cryptoMethods')}
                    </h3>
                    <ScPayments>
                      {cryptoMethods.map((method, index) => (
                        <PaymentMethodCard
                          key={method.id}
                          method={method}
                          index={index}
                          onSelect={onSelect}
                          currentMethod={currentMethod}
                        />
                      ))}
                    </ScPayments>
                  </>
                )}
              </>
            )}
          </>
        )}
      </ScPaymentsMethods>
    );
  }
);
